/**
 * WorkDetailOS — Mac AIOS hissinde Work Detail bileşeni
 * Expert agent panel, Anne/Baba durumu, status timeline
 */
import axios from 'axios'
import {
  Bot, CheckCircle2, Clock, Cpu, Crown, DollarSign,
  FileText, Loader2, Shield, XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'
import type { WorkTask, WorkResultResponse } from '../../types/work'

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` })

interface BabaMeta {
  expert_agent_id?: number
  anne_eq_score?: number
  anne_note?: string
  anne_approved?: number
  baba_priority?: number
}

interface ExpertInfo {
  id: number
  agent_name: string
  expertise?: string
  status?: string
}

// ─── Status Timeline ─────────────────────────────────────────────────────────
function StatusTimeline({ status, startedAt, completedAt }: {
  status: string; startedAt?: string; completedAt?: string
}) {
  const steps = [
    { key: 'pending', label: 'Queued', icon: <Clock size={12} /> },
    { key: 'processing', label: 'Processing', icon: <Loader2 size={12} /> },
    { key: 'completed', label: 'Completed', icon: <CheckCircle2 size={12} /> },
  ]
  const statusOrder = ['pending', 'processing', 'completed', 'failed']
  const currentIdx = statusOrder.indexOf(status)
  const isFailed = status === 'failed'

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 0,
      padding: '12px 16px', borderRadius: 12,
      background: 'var(--os-surface, rgba(255,255,255,0.03))',
      border: '1px solid var(--os-border, rgba(255,255,255,0.08))',
    }}>
      {steps.map((s, i) => {
        const done = currentIdx > i || (status === 'completed' && s.key === 'completed')
        const active = s.key === status
        const failed = isFailed && s.key === 'completed'
        const color = failed ? '#ef4444' : done ? '#10b981' : active ? '#6366f1' : 'var(--os-dim, #4b5563)'
        return (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: done ? `${color}20` : active ? `${color}15` : 'transparent',
                border: `1.5px solid ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color,
                animation: active && status === 'processing' ? 'spin 1.5s linear infinite' : undefined,
              }}>
                {failed ? <XCircle size={12} /> : s.icon}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color }}>{failed ? 'Failed' : s.label}</div>
                {s.key === 'processing' && startedAt && (
                  <div style={{ fontSize: 9, color: 'var(--os-dim)' }}>
                    {new Date(startedAt).toLocaleTimeString()}
                  </div>
                )}
                {s.key === 'completed' && completedAt && (
                  <div style={{ fontSize: 9, color: 'var(--os-dim)' }}>
                    {new Date(completedAt).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: 1.5, margin: '0 8px',
                background: done ? '#10b981' : 'var(--os-border, rgba(255,255,255,0.1))',
                borderRadius: 1,
              }} />
            )}
          </div>
        )
      })}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

// ─── Expert Agent Panel ──────────────────────────────────────────────────────
function ExpertPanel({ expertId }: { expertId?: number }) {
  const [expert, setExpert] = useState<ExpertInfo | null>(null)

  useEffect(() => {
    if (!expertId) {return}
    axios.get(`${API_URL}/api/agents/${expertId}`, { headers: authHdr() })
      .then(r => {
        const d = r.data as ExpertInfo | { agent: ExpertInfo }
        setExpert('agent' in d ? d.agent : d)
      })
      .catch(() => {})
  }, [expertId])

  if (!expertId) {return null}

  const hue = `hsl(${(expertId * 47) % 360},65%,48%)`

  return (
    <div style={{
      padding: '14px 16px', borderRadius: 12,
      background: 'var(--os-win-bg, #1c2128)',
      border: '1px solid var(--os-border, rgba(255,255,255,0.08))',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Bot size={13} color="#8b5cf6" />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)' }}>Assigned Expert</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: hue,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, color: '#fff',
          boxShadow: `0 4px 12px ${hue}40`,
        }}>
          {expert?.agent_name?.charAt(0) ?? '?'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--os-text)' }}>
            {expert?.agent_name ?? `Agent #${expertId}`}
          </div>
          {expert?.expertise && (
            <div style={{ fontSize: 10.5, color: 'var(--os-muted)', marginTop: 1 }}>
              {expert.expertise}
            </div>
          )}
        </div>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: expert?.status === 'active' ? '#10b981' : '#f59e0b',
          boxShadow: `0 0 6px ${expert?.status === 'active' ? '#10b981' : '#f59e0b'}60`,
        }} />
      </div>
    </div>
  )
}

// ─── Anne/Baba Panel ─────────────────────────────────────────────────────────
function AnneBabaPanel({ meta }: { meta: BabaMeta | null }) {
  if (!meta) {return null}

  return (
    <div style={{
      padding: '14px 16px', borderRadius: 12,
      background: 'linear-gradient(135deg, rgba(168,85,247,0.06), rgba(236,72,153,0.04))',
      border: '1px solid rgba(168,85,247,0.2)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Crown size={13} color="#a855f7" />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)' }}>Filiz Family</span>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        {/* Baba */}
        <div style={{
          flex: 1, padding: '10px 12px', borderRadius: 10,
          background: 'var(--os-surface, rgba(255,255,255,0.03))',
          border: '1px solid var(--os-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
            <div style={{
              width: 20, height: 20, borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700, color: '#fff',
            }}>B</div>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--os-text)' }}>Father Filiz</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--os-muted)' }}>
            Priority: <span style={{ color: '#f59e0b', fontWeight: 700 }}>{meta.baba_priority ?? 5}/10</span>
          </div>
        </div>
        {/* Anne */}
        <div style={{
          flex: 1, padding: '10px 12px', borderRadius: 10,
          background: 'var(--os-surface, rgba(255,255,255,0.03))',
          border: '1px solid var(--os-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
            <div style={{
              width: 20, height: 20, borderRadius: '50%',
              background: 'linear-gradient(135deg, #ec4899, #f472b6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700, color: '#fff',
            }}>A</div>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--os-text)' }}>Mother Filiz</span>
          </div>
          {meta.anne_eq_score !== null ? (
            <div style={{ fontSize: 10, color: 'var(--os-muted)' }}>
              EQ: <span style={{ color: '#10b981', fontWeight: 700 }}>{meta.anne_eq_score}/10</span>
              {' '}
              <Shield size={9} color={meta.anne_approved ? '#10b981' : '#f59e0b'} style={{ verticalAlign: 'middle' }} />
              {' '}
              <span style={{ color: meta.anne_approved ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                {meta.anne_approved ? 'Approved' : 'Pending'}
              </span>
            </div>
          ) : (
            <div style={{ fontSize: 10, color: 'var(--os-dim)' }}>Awaiting review</div>
          )}
          {meta.anne_note && (
            <div style={{ fontSize: 9.5, color: 'var(--os-muted)', marginTop: 4, fontStyle: 'italic' }}>
              {'“'}{meta.anne_note}{'”'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main Export ─────────────────────────────────────────────────────────────
export default function WorkDetailOS({ task }: {
  task: WorkTask
  result?: WorkResultResponse | null
}) {
  const [meta, setMeta] = useState<BabaMeta | null>(null)

  useEffect(() => {
    // Baba meta'yı çek — task ID'sinden eşleştir
    axios.get(`${API_URL}/api/baba/work/tasks?limit=100`, { headers: authHdr() })
      .then(r => {
        const resp = r.data as { tasks?: Array<any> }
        const tasks = resp.tasks ?? []
        // task.id = work_tasks.id, eşleştirilecek key: task ID'si
        const found = tasks.find((t: any) => t.id === task.id)
        if (found) {
          // response'dan baba metadata'sını ekle
          setMeta({
            expert_agent_id: found.expert_agent_id,
            anne_eq_score: found.anne_eq_score,
            anne_note: found.anne_note,
            anne_approved: found.anne_approved,
            baba_priority: found.baba_priority,
          })
        }
      })
      .catch(() => {
        // API hata varsa → meta null kalır, panel gizlenir
      })
  }, [task.id])

  const processingMs = task.processing_started_at && task.processing_completed_at
    ? new Date(task.processing_completed_at).getTime() - new Date(task.processing_started_at).getTime()
    : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Status Timeline ─────────────── */}
      <StatusTimeline
        status={task.status}
        startedAt={task.processing_started_at}
        completedAt={task.processing_completed_at}
      />

      {/* ── Meta Cards Row ──────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {[
          { icon: <FileText size={13} />, label: 'Category', value: task.category.replace('_', ' '), color: '#3b82f6' },
          { icon: <DollarSign size={13} />, label: 'Cost', value: `${task.flz_cost.toFixed(2)} FLZ`, color: '#10b981' },
          { icon: <Cpu size={13} />, label: 'Model', value: task.cf_model?.split('/').pop() ?? 'Auto', color: '#8b5cf6' },
          { icon: <Clock size={13} />, label: 'Duration', value: processingMs ? `${(processingMs / 1000).toFixed(1)}s` : '—', color: '#f59e0b' },
        ].map(m => (
          <div key={m.label} style={{
            padding: '10px 12px', borderRadius: 10,
            background: 'var(--os-win-bg, #1c2128)',
            border: '1px solid var(--os-border, rgba(255,255,255,0.08))',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
              <span style={{ color: m.color }}>{m.icon}</span>
              <span style={{ fontSize: 10, color: 'var(--os-muted)' }}>{m.label}</span>
            </div>
            <div style={{
              fontSize: 13, fontWeight: 700, color: 'var(--os-text)',
              fontFamily: m.label === 'Model' ? 'monospace' : undefined,
            }}>
              {m.value}
            </div>
          </div>
        ))}
      </div>

      {/* ── Expert + Anne/Baba side by side ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <ExpertPanel expertId={meta?.expert_agent_id} />
        <AnneBabaPanel meta={meta} />
      </div>

      {/* ── Input Preview ──────────────── */}
      {task.input_text && (
        <div style={{
          padding: '14px 16px', borderRadius: 12,
          background: 'var(--os-win-bg, #1c2128)',
          border: '1px solid var(--os-border, rgba(255,255,255,0.08))',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--os-muted)', marginBottom: 8 }}>
            Input
          </div>
          <div style={{
            fontSize: 12, lineHeight: 1.6, color: 'var(--os-text)',
            maxHeight: 120, overflowY: 'auto',
            padding: '10px 12px', borderRadius: 8,
            background: 'var(--os-surface, rgba(255,255,255,0.02))',
            border: '1px solid var(--os-border)',
            fontFamily: 'monospace', whiteSpace: 'pre-wrap',
          }}>
            {task.input_text}
          </div>
        </div>
      )}
    </div>
  )
}
